import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Heading,
  VStack,
  useToast,
  Text,
  FormHelperText,
  Container,
  Alert,
  AlertIcon,
  AlertTitle,
  RadioGroup,
  Radio,
  Stack,
  Spinner,
  FormErrorMessage,
  HStack,
} from '@chakra-ui/react';
import { submitTerminationInfo } from '../services/nihTerminationService';

const TerminationForm = () => {
  // Form state
  const [formData, setFormData] = useState({
    awardNumber: '',
    terminationNotice: '',
    contactEmail: '',
    qualitativeResponse: '',
    riskAssessment: '3',
  });

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const toast = useToast();

  const handleShareOnLinkedIn = () => {
    const linkedInShareUrl = `https://www.linkedin.com/shareArticle?url=https://terminations.haw.sh/`;
    window.open(linkedInShareUrl, '_blank');
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.awardNumber) {
      errors.awardNumber = 'Award number is required';
    }
    
    if (!formData.terminationNotice) {
      errors.terminationNotice = 'Termination notice content is required';
    }
    
    if (formData.contactEmail && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.contactEmail)) {
      errors.contactEmail = 'Invalid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Event handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleRiskAssessmentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      riskAssessment: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Form Error',
        description: 'Please check the form for errors.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }


    setIsSubmitting(true);

    try {
      // Convert riskAssessment to integer before submission
      const submissionData = {
        ...formData,
        riskAssessment: parseInt(formData.riskAssessment, 10),
      };

      const result = await submitTerminationInfo(submissionData);
      
      if (result.success) {
        setFormData({
          awardNumber: '',
          terminationNotice: '',
          contactEmail: '',
          qualitativeResponse: '',
          riskAssessment: '3',
        });
        setShowSuccess(true);
      } else {
        toast({
          title: 'Submission Error',
          description: result.error || 'There was an error submitting your information. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Submission Error',
        description: error.message || 'There was an error submitting your information. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isSubmitting) {
    return (
      <Container maxW="800px" py={10}>
        <VStack spacing={4} align="center">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text>Submitting your information...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="800px">
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading as="h1" size="xl" textAlign="center" mb={4}>
            Share Your NIH Grant Termination Information
          </Heading>
          <Text textAlign="center" fontSize="lg" mb={8}>
            Help the research community understand the impact of recent NIH grant terminations
            by sharing your experience. All submissions are anonymous unless you provide contact information.
          </Text>
        </Box>

        {showSuccess ? (
          <Alert
            status="success"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="md"
            p={6}
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={3} fontSize="lg">
              Information Submitted Successfully!
            </AlertTitle>
            <Text mb={4}>
              Thank you for sharing your experience. Your contribution helps the community understand
              the impact of these terminations.
            </Text>
            <Box mt={4}>
              <HStack spacing={4} justify="center">
                <Button
                  colorScheme="blue"
                  onClick={handleShareOnLinkedIn}
                >
                  Share on LinkedIn
                </Button>
              </HStack>
            </Box>
          </Alert>
        ) : (
          <Box as="form" onSubmit={handleSubmit} bg="white" p={8} borderRadius="md" boxShadow="md">
            <VStack spacing={5} align="stretch">
              <FormControl isRequired isInvalid={!!formErrors.awardNumber}>
                <FormLabel>NIH Award Number</FormLabel>
                <Input
                  type="text"
                  name="awardNumber"
                  value={formData.awardNumber}
                  onChange={handleChange}
                  placeholder="e.g. R01AI123456"
                />
                <FormHelperText>
                  The full grant identifier (e.g., R01AI123456)
                </FormHelperText>
                <FormErrorMessage>{formErrors.awardNumber}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!formErrors.terminationNotice}>
                <FormLabel>Termination Notice Content</FormLabel>
                <Textarea
                  name="terminationNotice"
                  value={formData.terminationNotice}
                  onChange={handleChange}
                  placeholder="Please paste the content of your termination notice here"
                  rows={6}
                />
                <FormHelperText>
                  Copy and paste the relevant parts of the termination notice you received
                </FormHelperText>
                <FormErrorMessage>{formErrors.terminationNotice}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!formErrors.contactEmail}>
                <FormLabel>Contact Email (Optional)</FormLabel>
                <Input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  placeholder="your.email@institution.edu"
                />
                <FormHelperText>
                  Only provide if you're willing to be contacted for follow-up
                </FormHelperText>
                <FormErrorMessage>{formErrors.contactEmail}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Prior Risk Assessment</FormLabel>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  How likely did you think your grant was at risk of termination before receiving the notice?
                </Text>
                <RadioGroup onChange={handleRiskAssessmentChange} value={formData.riskAssessment}>
                  <Stack direction={['column', 'row']} spacing={4} justify="space-between">
                    <Radio value="1">1 - Very Unlikely</Radio>
                    <Radio value="2">2 - Unlikely</Radio>
                    <Radio value="3">3 - Neutral</Radio>
                    <Radio value="4">4 - Likely</Radio>
                    <Radio value="5">5 - Very Likely</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Your Experience (Optional)</FormLabel>
                <Textarea
                  name="qualitativeResponse"
                  value={formData.qualitativeResponse}
                  onChange={handleChange}
                  placeholder="Please share 1-2 sentences about how this termination has impacted your research or career"
                  rows={3}
                />
                <FormHelperText>
                  This helps us understand the qualitative impact of these terminations
                </FormHelperText>
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isSubmitting}
                loadingText="Submitting"
                size="lg"
              >
                Submit Information
              </Button>
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default TerminationForm;